import React, { useId, useMemo } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ChartPanel, { ChartErrorBoundary } from './ChartPanel';

const fmt = (v, d = 0) =>
  v != null ? Number(v).toLocaleString('ru-RU', { maximumFractionDigits: d }) : '—';

const TOOLTIP_STYLE = {
  backgroundColor: 'rgba(30, 41, 59, 0.95)',
  border: 'none',
  borderRadius: '10px',
  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
  padding: '12px 14px',
};

const KPI_CARD_CLASS =
  'bg-white rounded-lg shadow-sm border border-gray-100 px-4 py-3.5 hover:shadow-md transition-shadow h-full flex flex-col min-h-[92px]';

export function KpiGrid({ items = [] }) {
  if (!items.length) return null;
  const cols =
    items.length >= 6 ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6'
      : items.length > 2 ? 'grid-cols-2 md:grid-cols-4'
        : 'grid-cols-2';
  return (
    <div className={`grid gap-3 ${cols}`}>
      {items.map((item) => (
        <div key={item.label} className={KPI_CARD_CLASS}>
          <p className="text-xs font-medium text-gray-600">{item.label}</p>
          <p className="text-lg font-bold text-gray-900 mb-1 tabular-nums">{item.value}</p>
        </div>
      ))}
    </div>
  );
}

/**
 * График динамики в стиле вкладки WB (ComposedChart + Area + градиенты).
 *
 * @param {Object} props
 * @param {string} [props.title='Динамика']
 * @param {string} [props.subtitle]
 * @param {Array} props.data — точки с полем stat_date или label
 * @param {Array<{key:string,name:string,color:string,yAxisId?:string}>} props.lines
 */
export default function MpDailyChart({
  title = 'Динамика',
  subtitle,
  data = [],
  lines = [],
  height = 320,
  dateKey,
}) {
  const chartId = useId().replace(/:/g, '');
  const xKey = dateKey || (data[0]?.label != null ? 'label' : 'stat_date');
  const hasRight = lines.some((l) => l.yAxisId === 'right');
  const chartData = useMemo(() => data, [data]);

  const tickFormatter = (v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : v);

  return (
    <div className="rounded-xl border border-white/50 bg-white/95 backdrop-blur-sm shadow-sm p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>

      <ChartPanel height={height}>
        <ChartErrorBoundary label={title}>
          {chartData.length === 0 ? (
            <div className="flex items-center justify-center h-full text-sm text-gray-500">
              Нет данных за выбранный период
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={height} debounce={50}>
              <ComposedChart data={chartData} margin={{ top: 12, right: 12, left: 8, bottom: 8 }}>
                <defs>
                  {lines.map((line) => (
                    <linearGradient
                      key={line.key}
                      id={`${chartId}-fill-${line.key}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor={line.color} stopOpacity={0.55} />
                      <stop offset="70%" stopColor={line.color} stopOpacity={0.2} />
                      <stop offset="100%" stopColor={line.color} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey={xKey}
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickLine={false}
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={tickFormatter}
                />
                {hasRight && (
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={tickFormatter}
                  />
                )}
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  labelStyle={{ color: '#94a3b8', fontSize: 12 }}
                  itemStyle={{ color: '#f1f5f9', fontSize: 13 }}
                  labelFormatter={(label) => `Дата: ${label}`}
                  formatter={(value, name) => {
                    const line = lines.find((l) => l.name === name);
                    const decimals = line?.name?.includes('%') ? 1 : 0;
                    return [fmt(value, decimals), line?.name || name];
                  }}
                />
                <Legend wrapperStyle={{ paddingTop: 12 }} iconType="circle" iconSize={8} />
                {lines.map((line) => (
                  <Area
                    key={line.key}
                    yAxisId={line.yAxisId || 'left'}
                    type="monotone"
                    dataKey={line.key}
                    name={line.name}
                    fill={`url(#${chartId}-fill-${line.key})`}
                    stroke={line.color}
                    strokeWidth={2.5}
                    baseValue={0}
                    connectNulls
                    isAnimationActive={false}
                  />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </ChartErrorBoundary>
      </ChartPanel>
    </div>
  );
}

export { fmt as mpFmt };
